<template>
  <el-dialog
    v-model="visible"
    width="440px"
    class="forgot-password-dialog"
    :close-on-click-modal="false"
    destroy-on-close
    @closed="resetForm"
  >
    <template #header>
      <p class="m-0 pr-6 text-center text-[18px] font-semibold leading-[1.4] text-foreground">{{ t("resetPassword") }}</p>
      <!-- 三语切换入口已取消，文案跟随全站语言（当前强制英文） -->
      <!--
      <div class="mt-2.5 flex items-center justify-center gap-1">
        <button v-for="item in LANGS" :key="item.key" type="button" @click="setLocale(item.key)">
          {{ item.label }}
        </button>
      </div>
      -->
    </template>
    <div class="forgot-password-form space-y-4">
      <!-- 邮箱：验证码发往该邮箱；后端按邮箱定位账号（selectByEmail） -->
      <div class="relative">
        <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-muted-foreground">
          <Mail :size="18" />
        </div>
        <input
          name="email"
          v-model="form.email"
          type="email"
          autocomplete="email"
          :placeholder="t('emailPlaceholder')"
          class="forgot-input"
        />
      </div>

      <!-- 邮箱验证码行与 Create account 弹窗一致：输入框内嵌 Get code -->
      <div
        class="relative w-full h-[50px] bg-muted/40 rounded-[6px] border border-transparent focus-within:border-[#FF007F] transition-colors overflow-hidden group flex items-center"
      >
        <KeySquare
          :size="18"
          :stroke-width="1.5"
          class="absolute left-[12px] top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-[#FF007F] transition-colors"
        />
        <input
          name="code"
          v-model="form.code"
          type="text"
          inputmode="numeric"
          maxlength="6"
          autocomplete="one-time-code"
          :placeholder="t('emailCodePlaceholder')"
          class="flex-1 min-w-0 h-full bg-transparent pl-[40px] pr-[16px] outline-none text-foreground text-[16px] tracking-[2px] placeholder:tracking-normal placeholder:text-muted-foreground"
          @keyup.enter="submit"
        />
        <button
          type="button"
          class="shrink-0 h-[34px] mr-[8px] px-3 rounded-[5px] text-[13px] font-medium text-white transition-colors"
          :class="
            !isValidEmail || emailCountdown > 0 || isSendingEmail
              ? 'bg-[#FF007F]/40 cursor-not-allowed'
              : 'bg-[#FF007F] hover:bg-[#E00070] active:bg-[#C40064] cursor-pointer'
          "
          :disabled="!isValidEmail || emailCountdown > 0 || isSendingEmail"
          @click="sendEmailCode"
        >
          {{ emailCountdown > 0 ? tf("resendIn", { n: emailCountdown }) : isSendingEmail ? t("sending") : t("getCode") }}
        </button>
      </div>

      <!-- 新密码 -->
      <div class="relative">
        <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-muted-foreground">
          <Lock :size="18" />
        </div>
        <input
          name="newPassword"
          v-model="form.newPassword"
          :type="showPassword ? 'text' : 'password'"
          autocomplete="new-password"
          :placeholder="t('resetNewPasswordPlaceholder')"
          class="forgot-input forgot-input-with-eye"
        />
        <button
          type="button"
          class="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
          @click="showPassword = !showPassword"
        >
          <Eye v-if="showPassword" :size="18" />
          <EyeOff v-else :size="18" />
        </button>
      </div>

      <!-- 确认密码 -->
      <div class="relative">
        <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-muted-foreground">
          <Lock :size="18" />
        </div>
        <input
          name="confirmPassword"
          v-model="form.confirmPassword"
          :type="showConfirmPassword ? 'text' : 'password'"
          autocomplete="new-password"
          :placeholder="t('resetConfirmPasswordPlaceholder')"
          class="forgot-input forgot-input-with-eye"
          @keyup.enter="submit"
        />
        <button
          type="button"
          class="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
          @click="showConfirmPassword = !showConfirmPassword"
        >
          <Eye v-if="showConfirmPassword" :size="18" />
          <EyeOff v-else :size="18" />
        </button>
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end gap-3">
        <el-button @click="visible = false">{{ t("cancel") }}</el-button>
        <el-button class="reset-submit-btn" type="primary" :loading="isSubmitting" @click="submit">{{ t("confirmReset") }}</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts" setup>
import { computed, onUnmounted, reactive, ref, watch } from "vue";
import { ElMessage } from "element-plus";
import { Mail, KeySquare, Lock, Eye, EyeOff } from "@lucide/vue";
import { useI18n } from "@/i18n/useI18n";
import { httpPost, ServerType } from "@/service/httpRequest";
import { encryptPassword } from "@/service/passwordCrypto.js";

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    /** 登录弹窗中已填写的账号：仅当它本身是个合法邮箱时才回填邮箱框（历史值可能是用户名，不能当邮箱用） */
    defaultUsername?: string;
  }>(),
  {
    defaultUsername: "",
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "success"): void;
}>();

/** 海外邮箱：只做基本形态校验，与后端 EmailRegisterServiceImpl.EMAIL_RE 保持一致 */
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

/** 文案跟随全站语言；三语切换入口已取消 */
const { t, tf } = useI18n();

const visible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

const form = reactive({
  email: "",
  code: "",
  newPassword: "",
  confirmPassword: "",
});

const isSubmitting = ref(false);
const isSendingEmail = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);

const emailCountdown = ref(0);
let emailCountdownTimer: ReturnType<typeof setInterval> | null = null;

const isValidEmail = computed(() => EMAIL_RE.test(form.email.trim()));

/** 登录弹窗带入的账号：只有它本身是邮箱才回填，避免把用户名塞进邮箱框 */
const presetEmail = (): string => {
  const account = (props.defaultUsername || "").trim();
  return EMAIL_RE.test(account) ? account : "";
};

watch(
  () => props.modelValue,
  (open) => {
    if (!open) return;
    const email = presetEmail();
    if (email) {
      form.email = email;
    }
  },
);

const clearEmailCountdown = () => {
  if (emailCountdownTimer) {
    clearInterval(emailCountdownTimer);
    emailCountdownTimer = null;
  }
};

const startEmailCountdown = () => {
  clearEmailCountdown();
  emailCountdown.value = 60;
  emailCountdownTimer = setInterval(() => {
    emailCountdown.value -= 1;
    if (emailCountdown.value <= 0) {
      clearEmailCountdown();
    }
  }, 1000);
};

const resetForm = () => {
  form.email = presetEmail();
  form.code = "";
  form.newPassword = "";
  form.confirmPassword = "";
  showPassword.value = false;
  showConfirmPassword.value = false;
  clearEmailCountdown();
  emailCountdown.value = 0;
};

/** 向已注册邮箱发送重置密码验证码（后端要求邮箱必须已注册，否则报「登录账号不存在」） */
const sendEmailCode = () => {
  const email = form.email.trim();
  if (!EMAIL_RE.test(email)) {
    ElMessage.warning(t("invalidEmail"));
    return;
  }

  isSendingEmail.value = true;
  httpPost(ServerType.Base, { url: "/auth/email/send-reset-code", data: { email } })
    .then(() => {
      ElMessage.success(t("emailCodeSent"));
      startEmailCountdown();
    })
    .finally(() => {
      isSendingEmail.value = false;
    });
};

/** 校验邮箱与验证码后提交重置（验证码校验在后端，与邮箱注册同一套口径） */
const submit = () => {
  const email = form.email.trim();
  const code = form.code.trim();
  const newPassword = form.newPassword.trim();
  const confirmPassword = form.confirmPassword.trim();

  if (!email) {
    ElMessage.warning(t("enterEmail"));
    return;
  }
  if (!EMAIL_RE.test(email)) {
    ElMessage.warning(t("invalidEmail"));
    return;
  }
  if (!code) {
    ElMessage.warning(t("enterCode"));
    return;
  }
  if (newPassword.length < 6 || newPassword.length > 16) {
    ElMessage.warning(t("passwordLen616"));
    return;
  }
  if (newPassword !== confirmPassword) {
    ElMessage.warning(t("passwordMismatch"));
    return;
  }

  isSubmitting.value = true;
  Promise.all([encryptPassword(newPassword), encryptPassword(confirmPassword)])
    .then(([encryptedNewPassword, encryptedConfirmPassword]) =>
      httpPost(ServerType.Base, {
        url: "/auth/email/reset-password",
        data: {
          email,
          code,
          newPassword: encryptedNewPassword,
          confirmPassword: encryptedConfirmPassword,
        },
      }),
    )
    .then(() => {
      ElMessage.success(t("passwordResetOk"));
      visible.value = false;
      emit("success");
    })
    .finally(() => {
      isSubmitting.value = false;
    });
};

onUnmounted(() => {
  clearEmailCountdown();
});
</script>

<style lang="scss" scoped>
/* 表单沿用登录输入框结构；强调色固定为网站主题色 #ff007f，背景仍跟随亮/暗主题变量 */
.forgot-password-form {
  .forgot-input {
    display: flex;
    height: 50px;
    width: 100%;
    border-radius: 6px;
    border: 1px solid transparent;
    background: color-mix(in srgb, var(--muted) 30%, transparent);
    padding-left: 40px;
    padding-right: 16px;
    font-size: 16px;
    line-height: 1.5;
    color: var(--foreground);
    outline: none;
    transition: all 0.2s;

    &::placeholder {
      color: var(--muted-foreground);
    }

    &:focus,
    &:focus-visible {
      background: color-mix(in srgb, var(--muted) 50%, transparent);
      border-color: #ff007f;
      box-shadow: 0 0 0 2px color-mix(in srgb, #ff007f 10%, transparent);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  .forgot-input-with-eye {
    padding-right: 40px;
  }
}

:deep(.reset-submit-btn.el-button--primary) {
  background: #ff007f;
  border: none;
  box-shadow: 0 2px 8px rgb(255 0 127 / 25%);

  &:hover {
    background: #e00070;
    box-shadow: 0 4px 12px rgb(255 0 127 / 35%);
  }

  &:active {
    background: #c40064;
    box-shadow: 0 1px 4px rgb(255 0 127 / 20%);
  }

  &.is-disabled,
  &.is-loading {
    background: rgb(255 0 127 / 45%);
    box-shadow: none;
  }
}

</style>
